import { useState } from 'react';
import './SellerHelp.css';

const SellerHelp = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    { id: 'all', name: 'All Topics', icon: '📋', count: 7 },
    { id: 'development', name: 'App Development', icon: '💻', count: 3 },
    { id: 'business', name: 'Business & Payment', icon: '💼', count: 2 },
    { id: 'support', name: 'Support & Maintenance', icon: '🛠️', count: 2 }
  ];

  const faqs = [
    {
      id: 1,
      category: 'development',
      question: 'What kind of apps do you develop?',
      answer: 'I develop mobile and web applications ranging from simple utilities to complex business solutions, focusing on quality and usability.',
      tags: ['development', 'mobile', 'web', 'business']
    },
    {
      id: 2,
      category: 'business',
      question: 'How can I contact you for custom app development?',
      answer: 'You can reach me via email at qalvinahmad@gmail.com or through the contact form on my website.',
      tags: ['contact', 'custom', 'development', 'email']
    },
    {
      id: 3,
      category: 'support',
      question: 'Do you offer maintenance and updates?',
      answer: 'Yes, maintenance and app updates are available under separate agreements.',
      tags: ['maintenance', 'updates', 'support', 'agreement']
    },
    {
      id: 4,
      category: 'business',
      question: 'How do you handle payment and refunds?',
      answer: 'Payments are usually made in milestones, with an initial deposit required before work begins. Refunds are considered on a case-by-case basis, particularly if the project is canceled before significant work starts.',
      tags: ['payment', 'refunds', 'milestones', 'deposit']
    },
    {
      id: 5,
      category: 'development',
      question: 'What platforms do you develop for?',
      answer: 'I develop apps for Android, iOS, and web platforms.',
      tags: ['platforms', 'android', 'ios', 'web']
    },
    {
      id: 6,
      category: 'development',
      question: 'How secure are the applications you develop?',
      answer: 'I prioritize security by implementing industry best practices such as secure coding standards, data encryption, proper authentication mechanisms, and regular testing to minimize vulnerabilities and protect user data.',
      tags: ['security', 'encryption', 'authentication', 'testing']
    },
    {
      id: 7,
      category: 'support',
      question: 'How can I provide feedback or suggestions to you?',
      answer: 'You can send your feedback or suggestions anytime via email at qalvinahmad@gmail.com or through the contact form on my website. I highly appreciate user input to improve my services and applications.',
      tags: ['feedback', 'suggestions', 'contact', 'improvement']
    }
  ];

  const filteredFaqs = faqs.filter(faq => {
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         faq.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const quickActions = [
    {
      title: 'Start a Project',
      description: 'Get a quote for your custom app development',
      icon: '🚀',
      action: 'primary',
      link: '#'
    },
    {
      title: 'View Portfolio',
      description: 'Check out my previous work and projects',
      icon: '📱',
      action: 'secondary',
      link: '#'
    },
    {
      title: 'Technical Documentation',
      description: 'Access development guides and resources',
      icon: '📚',
      action: 'secondary',
      link: '#'
    }
  ];

  const handleSearchSubmit = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Scroll to FAQ section
      const faqSection = document.querySelector('.faq-section');
      if (faqSection) {
        faqSection.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  };

  return (
    <div className="seller-help">
      {/* Hero Section with Enhanced Visual Hierarchy */}
      <section className="help-hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-badge">
              <span className="badge-icon">💡</span>
              <span>Developer Support</span>
            </div>
            <h1 className="hero-title">
              How can I <span className="accent-text">help you</span> today?
            </h1>
            <p className="hero-subtitle">
              Find answers about my app development services, get project quotes, and learn about my development process
            </p>
            
            {/* Enhanced Search with Visual Feedback */}
            <div className="search-container">
              <div className="search-wrapper">
                <span className="search-icon">🔍</span>
                <input
                  type="text"
                  placeholder="Search for answers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleSearchSubmit}
                  className="search-input"
                />
                {searchQuery && (
                  <button 
                    className="clear-search"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>
              <div className="search-suggestions">
                <span className="suggestion-label">Popular:</span>
                {['app development', 'payment', 'platforms'].map(suggestion => (
                  <button
                    key={suggestion}
                    className="suggestion-chip"
                    onClick={() => setSearchQuery(suggestion)}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container">
    

        {/* Category Navigation with Cognitive Load Management */}
        <section className="help-categories-section">
          <div className="categories-header">
            <h2 className="section-title">Browse by Category</h2>
            <p className="section-subtitle">Choose a topic to find relevant answers quickly</p>
          </div>
          
          <div className="categories-nav">
            {categories.map(category => (
              <button
                key={category.id}
                className={`category-btn ${activeCategory === category.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(category.id)}
              >
                <span className="category-icon">{category.icon}</span>
                <span className="category-name">{category.name}</span>
                <span className="category-count">{category.count}</span>
              </button>
            ))}
          </div>
        </section>

        {/* FAQ Section with Enhanced UX Patterns */}
        <section className="faq-section">
          <div className="faq-header">
            <h2 className="section-title">
              {activeCategory === 'all' ? 'All Questions' : 
               categories.find(c => c.id === activeCategory)?.name}
            </h2>
            <div className="results-info">
              {filteredFaqs.length} {filteredFaqs.length === 1 ? 'result' : 'results'} found
            </div>
          </div>

          <div className="faq-list">
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map(faq => (
                <div key={faq.id} className={`faq-item ${expandedFaq === faq.id ? 'expanded' : ''}`}>
                  <button
                    className="faq-question"
                    onClick={() => setExpandedFaq(expandedFaq === faq.id ? null : faq.id)}
                    aria-expanded={expandedFaq === faq.id}
                  >
                    <h3 className="question-text">{faq.question}</h3>
                    <span className="expand-icon">
                      {expandedFaq === faq.id ? '−' : '+'}
                    </span>
                  </button>
                  
                  <div className="faq-answer">
                    <div className="answer-content">
                      <p>{faq.answer}</p>
                      <div className="faq-tags">
                        {faq.tags.map(tag => (
                          <span key={tag} className="faq-tag">{tag}</span>
                        ))}
                      </div>
                      <div className="faq-actions">
                        <button className="helpful-btn">
                          👍 Helpful
                        </button>
                        <button className="feedback-btn">
                          💬 Need more help?
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-results">
                <div className="no-results-icon">🔍</div>
                <h3>No results found</h3>
                <p>Try adjusting your search terms or browse different categories</p>
                <button 
                  className="reset-btn"
                  onClick={() => {
                    setSearchQuery('');
                    setActiveCategory('all');
                  }}
                >
                  Reset Filters
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Enhanced Contact Support with Better UX Writing */}
        <section className="contact-support-section">
          <div className="support-header">
            <h2 className="section-title">Still need help?</h2>
            <p className="section-subtitle">Let's discuss your project and how I can help bring your ideas to life</p>
          </div>
          
          <div className="support-options">
            <div className="support-card priority">
              <div className="support-icon">📧</div>
              <h3 className="support-title">Direct Contact</h3>
              <p className="support-description">Get personalized consultation for your project</p>
              <div className="support-meta">
                <span className="response-time">📅 Response within 24 hours</span>
              </div>
              <a href="mailto:qalvinahmad@gmail.com" className="support-btn primary">
                Send Email
                <span className="btn-icon">→</span>
              </a>
            </div>
            
            <div className="support-card">
              <div className="support-icon">💬</div>
              <h3 className="support-title">Project Consultation</h3>
              <p className="support-description">Message to discuss your requirements</p>
              <div className="support-meta">
                <span className="community-size">🚀 Free initial consultation</span>
              </div>
              <a 
                href="https://instagram.com/qalvinahmad" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="support-btn secondary"
              >
                Message
                <span className="btn-icon">→</span>
              </a>
            </div>
          </div>

          {/* Enhanced Feedback Section */}
          <div className="feedback-section">
            <div className="feedback-card">
              <h3>How was your experience with my services?</h3>
              <div className="rating-buttons">
                {[1, 2, 3, 4, 5].map(rating => (
                  <button key={rating} className="rating-btn" title={`${rating} stars`}>
                    ⭐
                  </button>
                ))}
              </div>
              <p className="feedback-subtitle">Your feedback helps me improve my services</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default SellerHelp;
